// What's New announcements for ClearView CRM.
// Newest entries first. Each entry must have a unique `id`; the
// WhatsNewSidebar uses it to track which items the user has seen.
//
// Schema:
//   id:          string  — unique, stable
//   date:        string  — ISO date (YYYY-MM-DD)
//   tag:         string? — short label shown as a pill (e.g. "NEW", "IMPROVED")
//   title:       string  — card heading
//   summary:     string? — one-paragraph description
//   details:     string[]? — bullet list of sub-points
//   ctaLabel:    string? — optional call-to-action button label
//   ctaView:     string? — view name passed to navigate() when CTA clicked
//   ctaOpenHelp: boolean? — when true, also opens the view's ? help panel
//                          on arrival so users can read the full workflow
//   pinned:      boolean? — stick to the top of the list regardless of date
//   guideId:     string?  — when set, the CTA opens that guide in the reader
//                          modal instead of navigating to a view

export const WHATS_NEW = [
  {
    id: "2026-04-21-my-day-sidebar",
    date: "2026-04-21",
    tag: "New",
    title: "My Day — your action sidebar on the Dashboard",
    summary:
      "A new collapsible sidebar on the Dashboard answering 'what do I need to do today?' at a glance. Tasks due, overdue touches in your portfolio, and your biggest capacity opportunities — surfaced so the revenue-protecting work can't scroll off the screen.",
    details: [
      "Three sections: Tasks Due (today + overdue), Overdue Touches (30+ days without contact), and Opportunity Gap (biggest capacity upside) — all scoped to donors assigned to you",
      "Top 3 per section plus a 'See all ›' link that deep-jumps into the Tasks, Cultivation, or Capacity view",
      "Collapse to a 44px strip that still shows the counts — out of sight, not out of mind",
      "Click the gear icon to hide sections you don't need or reorder them; choices persist per user",
    ],
  },
  {
    id: "2026-04-18-small-shops-guide",
    date: "2026-04-18",
    tag: "Featured Guide",
    pinned: true,
    title: "Capacity View for Small Shops",
    summary:
      "A practical guide for chapters that can't afford a wealth-screening subscription. Covers what wealth screens actually do, what the Capacity View replaces for free, the free public-record sources that fill most remaining gaps, and a Monday/Friday workflow that produces board-ready prospect strategy without a paid tool.",
    details: [
      "Honest side-by-side of what changes if you subscribe later",
      "When a wealth screen is genuinely worth the money (and when it isn't)",
      "A 7-minute read you can share with your ED or board",
    ],
    ctaLabel: "Read the guide",
    guideId: "capacity_for_small_shops",
  },
  {
    id: "2026-04-18-capacity-wealth-import",
    date: "2026-04-18",
    tag: "New",
    title: "Import wealth-screen data (DonorSearch, iWave, WealthEngine)",
    summary:
      "Settings → Data now accepts a CSV export from your wealth-screening vendor. Rows match to existing donors by email, then by name + city, with a preview step before anything is saved.",
    details: [
      "Matched rows overwrite in place on re-import — the raw row is preserved for audit",
      "Unmatched rows are dropped with a warning count",
      "Importing any data activates the External Wealth component (15% of confidence) for the whole chapter",
      "Understands '$1.2M', '$1,234,567', and 'n/a' — no need to clean the file first",
    ],
    ctaLabel: "Open Settings",
    ctaView: "settings",
  },
  {
    id: "2026-04-18-capacity-view-triage",
    date: "2026-04-18",
    tag: "New",
    title: "Capacity View — portfolio triage at a glance",
    summary:
      "A ranked list that answers 'where should I spend my time this week?' Sort by opportunity gap, filter by confidence, click any donor to assess them and log the next move — without leaving the page.",
    details: [
      "Four sort lenses: Opportunity Gap, Capacity, Confidence (lowest first), Last Contact",
      "System-suggested capacity bands from 5-year giving × 5 so the view is useful on day one",
      "Click 'At or above rating' in the Summary rail to isolate donors whose band may need raising",
      "Six-component confidence score with weight sliders in Settings → Scoring",
      "Click the ? in the top-right for the full workflow guide",
    ],
    ctaLabel: "Open Capacity View",
    ctaView: "capacity",
    ctaOpenHelp: true,
  },
  {
    id: "2026-04-17-capacity-tab-donor",
    date: "2026-04-17",
    tag: "New",
    title: "Capacity tab on every donor profile",
    summary:
      "A new tab on every donor's profile shows their estimated capacity band, a 0–100% confidence score explaining how defensible the estimate is, and the single next action that would strengthen it most.",
    details: [
      "Enter your own capacity band, inclination, confidence level, and narrative notes — the system surfaces observed signals alongside",
      "Confidence breakdown shows six weighted components with gap-analysis hints",
      "Wealth screen data appears here automatically when imported (see entry above)",
      "Opens directly when you arrive from the Capacity triage view",
    ],
  },
  {
    id: "2026-04-13-fiscal-year-pollen",
    date: "2026-04-13",
    tag: "Improved",
    title: "LYBNT & SYBNT respect fiscal year",
    summary:
      "Lapsed-donor pollen cards now use the chapter's fiscal year setting instead of calendar year.",
  },
  {
    id: "2026-04-12-batch-csv-import",
    date: "2026-04-12",
    tag: "New",
    title: "CSV import inside gift batches",
    summary:
      "Import a CSV directly into an open batch. Imported gifts carry the batch ID, so the batch report doubles as the import audit trail.",
    details: [
      "Fund matching is case- and punctuation-insensitive",
      "Print view fixes for the batch audit report",
    ],
    ctaLabel: "Open Gifts",
    ctaView: "gifts",
  },
  {
    id: "2026-04-10-lybnt-sybnt",
    date: "2026-04-10",
    tag: "New",
    title: "LYBNT & SYBNT pollen cards",
    summary:
      "Spot lapsed donors at a glance from the dashboard with dedicated pollen cards.",
  },
  {
    id: "2026-04-08-cmdk-nav",
    date: "2026-04-08",
    tag: "Improved",
    title: "Navigation shortcuts in Ctrl+K",
    summary:
      "Jump straight to any view from the command bar — no mouse required.",
  },
  {
    id: "2026-04-05-edit-contact-reports",
    date: "2026-04-05",
    tag: "New",
    title: "Edit existing contact reports",
    summary:
      "Correct typos and update details on previously logged contacts.",
  },
];
